
import { useEffect, useRef, useState } from 'react';

export default function NumbersSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ participants: 0, countries: 0, experiences: 0, panels: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
          animateCounters();
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const animateCounters = () => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    const targets = { participants: 100, countries: 20, experiences: 8, panels: 0 };
    let step = 0;

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounts({
        participants: Math.floor(targets.participants * progress),
        countries: Math.floor(targets.countries * progress),
        experiences: Math.floor(targets.experiences * progress),
        panels: 0
      });

      if (step >= steps) {
        setCounts(targets);
        clearInterval(timer);
      }
    }, interval);
  };

  const stats = [
    {
      number: counts.participants,
      suffix: '',
      label: 'Curated Participants',
      description: 'Carefully selected for alignment'
    },
    {
      number: counts.countries,
      suffix: '+',
      label: 'Countries Represented',
      description: 'A truly global gathering'
    },
    {
      number: counts.experiences,
      suffix: '',
      label: 'Immersive Experiences',
      description: 'Beyond traditional sessions'
    },
    {
      number: counts.panels,
      suffix: '',
      label: 'Generic Panels',
      description: 'Intimate conversations only'
    }
  ];

  return (
    <section ref={sectionRef} className="relative py-28 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-950 overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-champagne-500/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-champagne-500/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      {/* Decorative Lines */}
      <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-champagne-500/20 to-transparent"></div>
      <div className="absolute bottom-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-champagne-500/20 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Main Content - Two Column Layout */}
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          
          {/* Left Column - Header */}
          <div className={`lg:col-span-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <span className="text-champagne-400 text-xs font-sans font-medium tracking-[0.25em] uppercase">
              By The Numbers
            </span>
            
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-white mt-4 mb-6 leading-[1.1]">
              Summit<br />
              <span className="text-champagne-400">Impact</span>
            </h2>
            
            <div className="w-20 h-1 bg-gradient-to-r from-champagne-400 to-champagne-600 rounded-full mb-6"></div>
            
            <p className="text-navy-300 font-sans text-sm leading-relaxed">
              Every number tells a story of connection, innovation, and transformation.
            </p>
          </div>

          {/* Right Column - Stats */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`group relative p-8 rounded-2xl bg-white/[0.03] border border-white/[0.08] backdrop-blur-sm hover:bg-white/[0.06] hover:border-champagne-500/30 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${index * 100 + 200}ms` }}
                >
                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden rounded-tr-2xl">
                    <div className="absolute top-0 right-0 w-px h-8 bg-gradient-to-b from-champagne-500/50 to-transparent"></div>
                    <div className="absolute top-0 right-0 h-px w-8 bg-gradient-to-l from-champagne-500/50 to-transparent"></div>
                  </div>

                  <div className="flex items-start gap-5">
                    {/* Number */}
                    <div className="font-display text-5xl lg:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-champagne-300 via-champagne-400 to-champagne-500 leading-none">
                      {stat.number}{stat.suffix}
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 pt-2">
                      <h3 className="font-sans text-base font-semibold text-white mb-1 group-hover:text-champagne-300 transition-colors">
                        {stat.label}
                      </h3>
                      <p className="text-navy-400 font-sans text-xs leading-relaxed">
                        {stat.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className={`text-center mt-20 pt-12 border-t border-white/[0.08] transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-4">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-champagne-500/50"></div>
            <p className="font-display text-xl lg:text-2xl text-white/90 italic">
              "Curated, not crowded. Intimate, not institutional."
            </p>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-champagne-500/50"></div>
          </div>
        </div>
      </div>
    </section>
  );
}
